import { useEffect, useState } from 'react';
import type { TrackLevel } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { hub } from '@/lib/hub-client';
import { LevelSelect, RoleCheckList, toggleInSet, type RoleRef } from './grant-fields';
import { accessErrorMessage, deleteLevelRelations, deleteRoleMemberships, levelRelations } from './remove-user';

/** A row of the Access table — what Edit pre-fills from. */
export interface AccessUser {
  userId: string;
  email: string;
  name: string | null;
  relations: string[];
  level: TrackLevel | null;
  roles: RoleRef[];
}

interface FoundUser {
  id: string;
  email: string;
  name: string | null;
}

const trackParams = (trackId: string) => ({ objectType: 'track', objectId: trackId });

/**
 * Grant a level and/or roles to a user in ONE create (D7). Edit diffs: the
 * held level relations are deleted when the level changes, dropped roles go in
 * one delete, and the new level + added roles go in one create.
 */
export async function applyGrant(
  trackId: string,
  userId: string,
  existing: AccessUser | null,
  level: TrackLevel | null,
  roleIds: Set<string>,
): Promise<void> {
  const had = new Set(existing?.roles.map((r) => r.id) ?? []);
  const added = [...roleIds].filter((id) => !had.has(id));
  const removed = [...had].filter((id) => !roleIds.has(id));
  const levelChanged = level !== (existing?.level ?? null);

  if (existing && levelChanged) await deleteLevelRelations(trackId, userId, levelRelations(existing.relations));
  await deleteRoleMemberships(trackId, userId, removed);
  const relation = levelChanged && level ? level : undefined;
  if (relation === undefined && added.length === 0) return;
  await hub.objectsRelationCreate({
    params: trackParams(trackId),
    body: { ...(relation ? { relation } : {}), roleIds: added, subjectType: 'user', subjectId: userId },
  });
}

export interface AddUserDialogProps {
  open: boolean;
  trackId: string;
  roles: RoleRef[];
  /** Present = edit access for this user; absent = add a user by search. */
  existing?: AccessUser | null;
  onClose: () => void;
  onSaved: () => void;
}

export function AddUserDialog({ open, trackId, roles, existing, onClose, onSaved }: AddUserDialogProps) {
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<FoundUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [user, setUser] = useState<FoundUser | null>(null);
  const [level, setLevel] = useState<TrackLevel | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setMatches([]);
    setUser(existing ? { id: existing.userId, email: existing.email, name: existing.name } : null);
    setLevel(existing?.level ?? null);
    setSelected(new Set(existing?.roles.map((r) => r.id) ?? []));
    setError(null);
  }, [open, existing]);

  // Email search: ≥ 2 chars, 250 ms after the last keystroke.
  useEffect(() => {
    if (existing || query.trim().length < 2) {
      setMatches([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      setSearching(true);
      hub.objectsUserSearch({ params: trackParams(trackId), query: { email: query.trim() } })
        .then((res) => { if (!cancelled) setMatches(res.items); })
        .catch((err) => { if (!cancelled) setError((err as Error).message); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, trackId, existing]);

  const canSubmit = user !== null && (level !== null || selected.size > 0) && !busy;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      await applyGrant(trackId, user.id, existing ?? null, level, selected);
      onSaved();
      onClose();
    } catch (err) {
      setError(accessErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={existing ? 'Edit access' : 'Add an existing user'}
      description={existing ? `${existing.email} — change the level, the roles, or both.` : 'Search hub users by email. Pick a level, roles, or both.'}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="track-access-form" disabled={!canSubmit}>
            {busy ? 'Saving…' : existing ? 'Save' : 'Add user'}
          </Button>
        </>
      }
    >
      <form id="track-access-form" className="space-y-4" onSubmit={submit}>
        {!existing && (
          <div className="space-y-2">
            <Label htmlFor="access-user-search">User</Label>
            <Input
              id="access-user-search"
              autoComplete="off"
              autoFocus
              placeholder="Search by email"
              value={user ? user.email : query}
              readOnly={user !== null}
              onChange={(e) => setQuery(e.target.value)}
            />
            {user ? (
              <p className="text-xs text-muted-foreground">
                {user.name ?? user.email}{' '}
                <button type="button" className="underline" onClick={() => setUser(null)}>
                  change
                </button>
              </p>
            ) : searching ? (
              <p className="text-xs text-muted-foreground">Searching…</p>
            ) : matches.length > 0 ? (
              <ul className="max-h-40 overflow-y-auto rounded-[var(--radius-ctl)] border text-sm">
                {matches.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      className="flex w-full flex-col items-start px-3 py-1.5 text-left hover:bg-accent"
                      onClick={() => setUser(m)}
                    >
                      <span>{m.email}</span>
                      {m.name && <span className="text-xs text-muted-foreground">{m.name}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            ) : query.trim().length >= 2 ? (
              <p className="text-xs text-muted-foreground">No active user matches.</p>
            ) : null}
          </div>
        )}
        <LevelSelect id="access-level" value={level} onChange={setLevel} />
        <RoleCheckList
          roles={roles}
          selected={selected}
          onToggle={(id, on) => setSelected((prev) => toggleInSet(prev, id, on))}
        />
        {error && <p className="text-sm text-destructive">{error}</p>}
      </form>
    </Dialog>
  );
}
